import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { ArrowLeft, Calendar, Clock, User, FileText, CreditCard, Heart, Loader2 } from 'lucide-react';
import PolicyPanel from '../components/PolicyPanel';
import AppHeader from '../components/AppHeader';
import { BOOKING_TIERS } from '../constants/policies';

export default function BookAppointment() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [tierCode, setTierCode] = useState(BOOKING_TIERS[0].code);
  const [livePrices, setLivePrices] = useState({}); // { code: { price_inr, price_display } }
  const [pricesLoading, setPricesLoading] = useState(true);
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);

  const selectedTier = useMemo(
    () => BOOKING_TIERS.find((t) => t.code === tierCode) || BOOKING_TIERS[0],
    [tierCode],
  );
  const selectedPrice = livePrices[tierCode]?.price_inr ?? null;

  useEffect(() => {
    fetchProviders();
    loadRazorpayScript();
    fetchAllPrices();
  }, []);

  const loadRazorpayScript = () => {
    if (document.getElementById('razorpay-checkout-js')) return;
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/providers`);
      setProviders(response.data.filter((p) => p.is_available !== false));
    } catch (error) {
      toast.error('Failed to fetch providers');
    }
  };

  const fetchAllPrices = async () => {
    setPricesLoading(true);
    try {
      const results = await Promise.all(
        BOOKING_TIERS.map((tier) =>
          axios
            .get(`${API}/pricing/calculate`, {
              params: { session_type: tier.session_type, duration_minutes: tier.duration_minutes },
            })
            .then((res) => [tier.code, res.data])
            .catch(() => [tier.code, null]),
        ),
      );
      const map = {};
      results.forEach(([code, data]) => {
        if (data) map[code] = data;
      });
      setLivePrices(map);
    } finally {
      setPricesLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/appointments`, {
        provider_id: selectedProvider.id,
        // Keep legacy session_type label for back-compat with existing backend
        // logic that maps tier1/2/3. Real pricing is sourced from /api/pricing.
        session_type: selectedTier.session_type,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        notes: formData.notes,
      });

      setAppointmentId(response.data.id);
      setShowPayment(true);
      toast.success('Appointment created! Please proceed with payment.');
    } catch (error) {
      toast.error('Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedPrice) {
      toast.error('Price not available — please retry');
      return;
    }
    try {
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        appointment_id: appointmentId,
        amount: selectedPrice,
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        order_id: orderResponse.data.order_id,
        name: 'Veyonika',
        description: `${selectedTier.label}`,
        handler: async function (response) {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              appointment_id: appointmentId,
            });
            toast.success('Payment successful! Appointment confirmed.');
            navigate('/seeker-dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: { color: '#7C3AED' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  if (showPayment) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
        <div className="max-w-md w-full card p-8 text-center">
          <CreditCard size={64} className="mx-auto mb-6 text-primary" />
          <h2 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Complete Payment</h2>
          <p className="text-[#718096] mb-2">{selectedTier.label}</p>
          <p className="text-[#718096] mb-6">
            Amount to pay: <span className="text-2xl font-semibold text-primary">₹{selectedPrice ?? '—'}</span>
          </p>
          <button
            data-testid="pay-now-btn"
            onClick={handlePayment}
            className="btn-primary w-full mb-4"
          >
            Pay Now
          </button>
          <button
            onClick={() => navigate('/seeker-dashboard')}
            className="text-[#718096] hover:text-primary transition-colors text-sm"
          >
            Pay Later
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-veyonika-bg">
      <AppHeader
        title="Book Appointment"
        hasUnsavedChanges={!appointmentId && (!!selectedProvider || Object.values(formData || {}).some((v) => v && String(v).trim().length > 0))}
      />

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Session Type Selection */}
          <div className="card p-6" data-testid="tier-picker">
            <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4">Select Session Type</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {BOOKING_TIERS.map((tier) => {
                const live = livePrices[tier.code];
                const active = tierCode === tier.code;
                return (
                  <button
                    key={tier.code}
                    type="button"
                    data-testid={`select-tier-${tier.code}`}
                    onClick={() => setTierCode(tier.code)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      active ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="font-heading font-semibold text-[#2D3748]">{tier.label}</span>
                      <span className="text-xl font-bold text-primary" data-testid={`tier-price-${tier.code}`}>
                        {pricesLoading ? (
                          <Loader2 size={16} className="animate-spin inline" />
                        ) : live ? (
                          live.is_free ? 'FREE' : `₹${live.price_inr}`
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-[#718096]">{tier.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider Selection */}
          <div className="card p-6">
            <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4">
              <User size={20} className="inline mr-2" />
              Select Counsellor
            </h3>
            <div className="space-y-3">
              {providers.length === 0 ? (
                <p className="text-[#718096] text-center py-4">No counsellors available</p>
              ) : (
                providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    data-testid={`select-provider-${provider.id}`}
                    onClick={() => setSelectedProvider(provider)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      selectedProvider?.id === provider.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <div className="font-medium text-[#2D3748]">Dr. {provider.name}</div>
                    {provider.specialization && (
                      <div className="text-sm text-[#718096] mt-1">{provider.specialization}</div>
                    )}
                    {provider.certification && (
                      <div className="text-xs text-[#A0AEC0] mt-1">Cert: {provider.certification}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Date and Time */}
          <div className="card p-6">
            <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4">
              <Calendar size={20} className="inline mr-2" />
              Schedule
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2D3748] mb-2">Date</label>
                <input
                  type="date"
                  name="scheduled_date"
                  data-testid="date-input"
                  value={formData.scheduled_date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2D3748] mb-2">
                  <Clock size={16} className="inline mr-1" />
                  Time
                </label>
                <input
                  type="time"
                  name="scheduled_time"
                  data-testid="time-input"
                  value={formData.scheduled_time}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card p-6">
            <h3 className="text-xl font-heading font-semibold text-[#2D3748] mb-4">
              <FileText size={20} className="inline mr-2" />
              Additional Notes (Optional)
            </h3>
            <textarea
              name="notes"
              data-testid="notes-input"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 rounded-xl"
              placeholder="Any specific concerns or topics you'd like to discuss..."
            />
          </div>

          {/* Cancellation / refund policy */}
          <PolicyPanel />

          {/* Price summary + submit */}
          <div className="card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-sm text-[#718096]">You will pay</div>
              <div className="text-3xl font-bold text-primary" data-testid="booking-total-price">
                {pricesLoading
                  ? '…'
                  : selectedPrice != null
                  ? `₹${selectedPrice}`
                  : '—'}
              </div>
              <div className="text-xs text-[#A0AEC0]">{selectedTier.label}</div>
            </div>
            <button
              type="submit"
              data-testid="book-appointment-submit-btn"
              disabled={loading || !selectedPrice}
              className="btn-primary w-full md:w-auto px-8 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating…' : 'Book Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
